import { useCallback, useEffect, useRef, useState } from "react";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import {
    assetFromUnderlying,
    buildChainsFromContracts,
    decNum,
    normalizeOrderbookLevels,
    underlyingKeyFromAsset,
    underlyingsFromMarketOverview,
} from "./optionsDataHelpers";
import { acquireOptionsSocket, getLastOptionsAccountUpdate, releaseOptionsSocket, setLastOptionsAccountUpdate } from "./optionsSocketManager";

export const OPTIONS_CHANNELS = {
    MARKET_OVERVIEW: "options:market_overview",
    CONTRACTS: "options:contracts",
    ACCOUNT: "options:account",
    CONTRACT_DETAIL: "options:contract_detail",
    USER_ORDERS: "options:user_orders",
    USER_POSITIONS: "options:user_positions",
};

function normalizeUserOrdersPayload(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.orders)) return data.orders;
    return [];
}

function normalizeUserPositionsPayload(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.positions)) return data.positions;
    return [];
}

function matchesContractDetailSymbol(updateSymbol, activeSymbol) {
    if (!activeSymbol) return false;
    if (!updateSymbol) return true;
    return String(updateSymbol).toUpperCase() === String(activeSymbol).toUpperCase();
}

function normalizeJwt(raw) {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    return s.replace(/^Bearer\s+/i, "");
}

function resolveAccessToken(tokenProp) {
    const fromProp = normalizeJwt(tokenProp);
    if (fromProp) return fromProp;
    try {
        return normalizeJwt(localStorage.getItem("token") || sessionStorage.getItem("token"));
    } catch {
        return null;
    }
}

function resolveOptionsExpiry(selectedExpiry, expiryDatesList) {
    if (selectedExpiry === "ALL") return "ALL";
    if (selectedExpiry) return selectedExpiry;
    return expiryDatesList?.length ? expiryDatesList[0] : "ALL";
}

/**
 *
 * Guest: connect without auth → market_overview, contracts, contract_detail.
 * Authed: same + account_update, user_orders_update, user_positions_update
 *   (unless individual channel flags are disabled — e.g. Market Options tab).
 */
export default function useOptionsWebSocket({
    token,
    selectedAsset = "",
    selectedExpiry = "ALL",
    selectedContractSymbol = null,
    enabled = true,
    subscribeMarketOverview = true,
    subscribeContracts = true,
    subscribeAccount = true,
    subscribeUserOrders = true,
    subscribeUserPositions = true,
    subscribeContractDetail = true,
}) {
    const socketRef = useRef(null);
    const subscribedRef = useRef({
        overview: false,
        contracts: false,
        account: false,
        contractDetail: null,
        userOrders: false,
        userPositions: false,
    });
    const contractsFilterRef = useRef({ underlying: null, expiry: "ALL" });
    const contractDetailSymbolRef = useRef(null);

    const [isConnected, setIsConnected] = useState(false);
    const [marketOverview, setMarketOverview] = useState(null);
    const [contractsPayload, setContractsPayload] = useState(null);
    const [accountUpdate, setAccountUpdate] = useState(() => getLastOptionsAccountUpdate());
    const [orderbookUpdate, setOrderbookUpdate] = useState(null);
    const [recentTrades, setRecentTrades] = useState([]);
    const [userOrders, setUserOrders] = useState([]);
    const [userPositions, setUserPositions] = useState([]);
    const [userOrdersReady, setUserOrdersReady] = useState(false);
    const [userPositionsReady, setUserPositionsReady] = useState(false);
    const [lastError, setLastError] = useState(null);
    const [contractsReady, setContractsReady] = useState(false);

    const accessToken = resolveAccessToken(token);
    const isAuthenticated = Boolean(accessToken);
    const underlying = underlyingKeyFromAsset(selectedAsset);
    const expiryDatesList = marketOverview?.expiry_dates?.[underlying] || [];
    const expiryDatesKey = expiryDatesList.length ? expiryDatesList.join("|") : "";
    const resolvedExpiry = resolveOptionsExpiry(selectedExpiry, expiryDatesList);

    const emitSubscribe = useCallback((payload) => {
        if (socketRef.current?.connected) {
            socketRef.current.emit("subscribe", payload);
        }
    }, []);

    const emitUnsubscribe = useCallback((payload) => {
        if (socketRef.current?.connected) {
            socketRef.current.emit("unsubscribe", payload);
        }
    }, []);

    const emitUpdate = useCallback((payload) => {
        if (socketRef.current?.connected) {
            socketRef.current.emit("update", payload);
        }
    }, []);

    // Clear private state when logged out
    useEffect(() => {
        if (!isAuthenticated) {
            setAccountUpdate(null);
            setLastOptionsAccountUpdate(null);
            setUserOrders([]);
            setUserPositions([]);
            setUserOrdersReady(false);
            setUserPositionsReady(false);
        }
    }, [isAuthenticated]);

    // ── Step 1: Shared /options socket (singleton, ref-counted) ──
    useEffect(() => {
        if (!enabled || DISABLE_ALL_NETWORK) {
            return undefined;
        }

        const socket = acquireOptionsSocket(accessToken);
        socketRef.current = socket;

        const cachedAccount = getLastOptionsAccountUpdate();
        if (cachedAccount) setAccountUpdate(cachedAccount);

        const resubscribeAll = () => {
            if (subscribedRef.current.overview) {
                socket.emit("subscribe", { channel: OPTIONS_CHANNELS.MARKET_OVERVIEW });
            }
            if (subscribedRef.current.contracts && contractsFilterRef.current.underlying) {
                socket.emit("subscribe", {
                    channel: OPTIONS_CHANNELS.CONTRACTS,
                    underlying: contractsFilterRef.current.underlying,
                    expiry: contractsFilterRef.current.expiry || "ALL",
                });
            }
            if (accessToken && subscribedRef.current.account) {
                socket.emit("subscribe", { channel: OPTIONS_CHANNELS.ACCOUNT });
            }
            if (accessToken && subscribedRef.current.userOrders) {
                socket.emit("subscribe", { channel: OPTIONS_CHANNELS.USER_ORDERS });
            }
            if (accessToken && subscribedRef.current.userPositions) {
                socket.emit("subscribe", { channel: OPTIONS_CHANNELS.USER_POSITIONS });
            }
            if (subscribedRef.current.contractDetail) {
                socket.emit("subscribe", {
                    channel: OPTIONS_CHANNELS.CONTRACT_DETAIL,
                    symbol: subscribedRef.current.contractDetail,
                });
            }
        };

        const onConnect = () => {
            setIsConnected(true);
            setLastError(null);
            const cached = getLastOptionsAccountUpdate();
            if (cached) setAccountUpdate(cached);
            resubscribeAll();
        };

        const onDisconnect = () => {
            setIsConnected(false);
            setMarketOverview(null);
            setContractsPayload(null);
            setAccountUpdate(getLastOptionsAccountUpdate());
            setOrderbookUpdate(null);
            setRecentTrades([]);
            setUserOrders([]);
            setUserPositions([]);
            setUserOrdersReady(false);
            setUserPositionsReady(false);
            contractDetailSymbolRef.current = null;
            setContractsReady(false);
        };

        const onConnectError = (err) => {
            setIsConnected(false);
            setLastError(err?.message || "Connection failed");
        };

        const onError = (err) => {
            const code = typeof err === "object" ? err?.code : null;
            if (code === "UNAUTHORIZED") return;
            const msg = typeof err === "string" ? err : err?.message;
            if (msg) setLastError(msg);
        };

        const onSubscribed = () => setLastError(null);

        const onMarketOverview = (data) => {
            if (data && typeof data === "object") setMarketOverview(data);
        };

        const onContractsUpdate = (data) => {
            if (data && typeof data === "object") {
                setContractsPayload(data);
                setContractsReady(true);
            }
        };

        const onAccountUpdate = (data) => {
            const normalized = setLastOptionsAccountUpdate(data);
            if (normalized) setAccountUpdate(normalized);
        };

        const onOrderbookUpdate = (data) => {
            if (!data || typeof data !== "object") return;
            const active = contractDetailSymbolRef.current;
            if (!matchesContractDetailSymbol(data.symbol, active)) return;
            setOrderbookUpdate(data);
        };

        const onRecentTradesUpdate = (trades) => {
            if (!contractDetailSymbolRef.current) return;
            setRecentTrades(Array.isArray(trades) ? trades : []);
        };

        const onUserOrdersUpdate = (orders) => {
            setUserOrders(normalizeUserOrdersPayload(orders));
            setUserOrdersReady(true);
        };

        const onUserPositionsUpdate = (positions) => {
            setUserPositions(normalizeUserPositionsPayload(positions));
            setUserPositionsReady(true);
        };

        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("connect_error", onConnectError);
        socket.on("error", onError);
        socket.on("subscribed", onSubscribed);
        socket.on("market_overview", onMarketOverview);
        socket.on("contracts_update", onContractsUpdate);
        socket.on("account_update", onAccountUpdate);
        socket.on("orderbook_update", onOrderbookUpdate);
        socket.on("recent_trades_update", onRecentTradesUpdate);
        socket.on("user_orders_update", onUserOrdersUpdate);
        socket.on("user_positions_update", onUserPositionsUpdate);

        if (socket.connected) {
            setIsConnected(true);
        }

        return () => {
            const sock = socketRef.current;
            // Unsubscribe every active channel before releasing listeners — do not disconnect.
            if (sock?.connected) {
                if (subscribedRef.current.overview) {
                    sock.emit("unsubscribe", { channel: OPTIONS_CHANNELS.MARKET_OVERVIEW });
                }
                if (subscribedRef.current.contracts) {
                    sock.emit("unsubscribe", { channel: OPTIONS_CHANNELS.CONTRACTS });
                }
                if (subscribedRef.current.account) {
                    sock.emit("unsubscribe", { channel: OPTIONS_CHANNELS.ACCOUNT });
                }
                if (subscribedRef.current.userOrders) {
                    sock.emit("unsubscribe", { channel: OPTIONS_CHANNELS.USER_ORDERS });
                }
                if (subscribedRef.current.userPositions) {
                    sock.emit("unsubscribe", { channel: OPTIONS_CHANNELS.USER_POSITIONS });
                }
                if (subscribedRef.current.contractDetail) {
                    sock.emit("unsubscribe", {
                        channel: OPTIONS_CHANNELS.CONTRACT_DETAIL,
                        symbol: subscribedRef.current.contractDetail,
                    });
                }
            }
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            socket.off("connect_error", onConnectError);
            socket.off("error", onError);
            socket.off("subscribed", onSubscribed);
            socket.off("market_overview", onMarketOverview);
            socket.off("contracts_update", onContractsUpdate);
            socket.off("account_update", onAccountUpdate);
            socket.off("orderbook_update", onOrderbookUpdate);
            socket.off("recent_trades_update", onRecentTradesUpdate);
            socket.off("user_orders_update", onUserOrdersUpdate);
            socket.off("user_positions_update", onUserPositionsUpdate);
            socketRef.current = null;
            subscribedRef.current = {
                overview: false,
                contracts: false,
                account: false,
                contractDetail: null,
                userOrders: false,
                userPositions: false,
            };
            contractsFilterRef.current = { underlying: null, expiry: "ALL" };
            contractDetailSymbolRef.current = null;
            releaseOptionsSocket();
        };
    }, [enabled, accessToken]);

    // ── Step 2: Subscribe market overview (public) ──
    useEffect(() => {
        if (!isConnected || !subscribeMarketOverview) return undefined;
        subscribedRef.current.overview = true;
        emitSubscribe({ channel: OPTIONS_CHANNELS.MARKET_OVERVIEW });
        return () => {
            subscribedRef.current.overview = false;
            emitUnsubscribe({ channel: OPTIONS_CHANNELS.MARKET_OVERVIEW });
        };
    }, [isConnected, subscribeMarketOverview, emitSubscribe, emitUnsubscribe]);

    // ── Step 3–4: Subscribe / update contracts (public) ──
    useEffect(() => {
        if (!isConnected || !subscribeContracts || !underlying) return undefined;

        const expiry = resolvedExpiry;
        const prev = contractsFilterRef.current;
        const filterChanged =
            prev.underlying !== underlying ||
            prev.expiry !== expiry;

        if (!filterChanged && subscribedRef.current.contracts) {
            return undefined;
        }

        setContractsReady(false);
        setContractsPayload(null);

        if (prev.underlying && subscribedRef.current.contracts && filterChanged) {
            contractsFilterRef.current = { underlying, expiry };
            emitUpdate({
                channel: OPTIONS_CHANNELS.CONTRACTS,
                underlying,
                expiry,
            });
            return undefined;
        }

        contractsFilterRef.current = { underlying, expiry };
        subscribedRef.current.contracts = true;
        emitSubscribe({
            channel: OPTIONS_CHANNELS.CONTRACTS,
            underlying,
            expiry,
        });

        return () => {
            subscribedRef.current.contracts = false;
            contractsFilterRef.current = { underlying: null, expiry: "ALL" };
            emitUnsubscribe({ channel: OPTIONS_CHANNELS.CONTRACTS });
        };
    }, [isConnected, subscribeContracts, underlying, resolvedExpiry, expiryDatesKey, emitSubscribe, emitUnsubscribe, emitUpdate]);

    // ── Step 4: Account summary (JWT only) ──
    useEffect(() => {
        if (!isConnected || !isAuthenticated || !subscribeAccount) return undefined;
        subscribedRef.current.account = true;
        emitSubscribe({ channel: OPTIONS_CHANNELS.ACCOUNT });
        return () => {
            subscribedRef.current.account = false;
            emitUnsubscribe({ channel: OPTIONS_CHANNELS.ACCOUNT });
        };
    }, [isConnected, isAuthenticated, subscribeAccount, emitSubscribe, emitUnsubscribe]);

    // ── Step 5: Contract detail — orderbook + trades (public) ──
    useEffect(() => {
        if (!isConnected || !subscribeContractDetail) return undefined;

        const prevSymbol = subscribedRef.current.contractDetail;

        if (prevSymbol && prevSymbol !== selectedContractSymbol) {
            emitUnsubscribe({
                channel: OPTIONS_CHANNELS.CONTRACT_DETAIL,
                symbol: prevSymbol,
            });
            setOrderbookUpdate(null);
            setRecentTrades([]);
        }

        if (!selectedContractSymbol) {
            subscribedRef.current.contractDetail = null;
            contractDetailSymbolRef.current = null;
            return undefined;
        }

        subscribedRef.current.contractDetail = selectedContractSymbol;
        contractDetailSymbolRef.current = selectedContractSymbol;
        emitSubscribe({
            channel: OPTIONS_CHANNELS.CONTRACT_DETAIL,
            symbol: selectedContractSymbol,
        });

        return () => {
            if (subscribedRef.current.contractDetail === selectedContractSymbol) {
                emitUnsubscribe({
                    channel: OPTIONS_CHANNELS.CONTRACT_DETAIL,
                    symbol: selectedContractSymbol,
                });
                subscribedRef.current.contractDetail = null;
                contractDetailSymbolRef.current = null;
            }
        };
    }, [isConnected, subscribeContractDetail, selectedContractSymbol, emitSubscribe, emitUnsubscribe]);

    // ── Step 6: Open orders (JWT only) ──
    useEffect(() => {
        if (!isConnected || !isAuthenticated || !subscribeUserOrders) return undefined;
        subscribedRef.current.userOrders = true;
        setUserOrdersReady(false);
        emitSubscribe({ channel: OPTIONS_CHANNELS.USER_ORDERS });
        return () => {
            subscribedRef.current.userOrders = false;
            emitUnsubscribe({ channel: OPTIONS_CHANNELS.USER_ORDERS });
        };
    }, [isConnected, isAuthenticated, subscribeUserOrders, emitSubscribe, emitUnsubscribe]);

    // ── Step 7: Open positions (JWT only) ──
    useEffect(() => {
        if (!isConnected || !isAuthenticated || !subscribeUserPositions) return undefined;
        subscribedRef.current.userPositions = true;
        setUserPositionsReady(false);
        emitSubscribe({ channel: OPTIONS_CHANNELS.USER_POSITIONS });
        return () => {
            subscribedRef.current.userPositions = false;
            emitUnsubscribe({ channel: OPTIONS_CHANNELS.USER_POSITIONS });
        };
    }, [isConnected, isAuthenticated, subscribeUserPositions, emitSubscribe, emitUnsubscribe]);

    /** Re-request live orderbook + open orders after place/cancel (WS snapshots are source of truth). */
    const refreshLiveTradeChannels = useCallback(() => {
        const socket = socketRef.current;
        if (!socket?.connected) return;

        const symbol = subscribedRef.current.contractDetail;
        if (symbol) {
            socket.emit("subscribe", {
                channel: OPTIONS_CHANNELS.CONTRACT_DETAIL,
                symbol,
            });
        }

        if (isAuthenticated && subscribedRef.current.userOrders) {
            socket.emit("subscribe", { channel: OPTIONS_CHANNELS.USER_ORDERS });
        }
    }, [isAuthenticated]);

    const underlyings = underlyingsFromMarketOverview(marketOverview);
    const expiryDates = expiryDatesList;
    const indexPrice = decNum(marketOverview?.index_prices?.[underlying]);

    const requestedExpiry = resolvedExpiry;
    const isContractsStale = Boolean(
        contractsPayload &&
        (contractsPayload.underlying !== underlying ||
            String(contractsPayload.expiry ?? "ALL") !== String(requestedExpiry))
    );

    const allChains = buildChainsFromContracts(
        isContractsStale ? [] : (contractsPayload?.contracts || []),
        indexPrice
    );

    const filteredChains = isContractsStale
        ? []
        : requestedExpiry !== "ALL"
            ? allChains.filter((c) => c.date === requestedExpiry)
            : allChains;

    const isContractsLoading = !contractsReady || isContractsStale || contractsPayload == null;

    const orderbook = orderbookUpdate
        && matchesContractDetailSymbol(orderbookUpdate.symbol, selectedContractSymbol)
        ? {
            symbol: orderbookUpdate.symbol,
            bids: normalizeOrderbookLevels(orderbookUpdate.bids),
            asks: normalizeOrderbookLevels(orderbookUpdate.asks),
            timestamp: orderbookUpdate.timestamp,
        }
        : null;

    return {
        isConnected,
        isAuthenticated,
        lastError,
        marketOverview,
        underlyings,
        expiryDates,
        indexPrice,
        contractsPayload,
        allChains: filteredChains,
        accountUpdate,
        orderbook,
        recentTrades: selectedContractSymbol ? recentTrades : [],
        userOrders,
        userPositions,
        underlying,
        assetFromUnderlying,
        isMarketLoading: marketOverview === null,
        isContractsLoading,
        isAccountLoading: subscribeAccount && isAuthenticated && accountUpdate === null,
        isUserOrdersLoading: subscribeUserOrders && isAuthenticated && isConnected && !userOrdersReady,
        isUserPositionsLoading: subscribeUserPositions && isAuthenticated && isConnected && !userPositionsReady,
        refreshLiveTradeChannels,
    };
}
